import { useState } from 'react';
import AppLogo from '../shared/AppLogo.jsx';
import { getDomainBranding } from '../../tenant/tenantConfig.js';

const PORTAL_THEME = {
  '--bg': '#f1f5f9',
  '--bg2': '#e8eef5',
  '--card': '#ffffff',
  '--fg': '#0f172a',
  '--fg2': '#1e293b',
  '--fg3': '#64748b',
  '--fg4': '#94a3b8',
  '--border': '#e2e8f0',
  '--accent': '#0284c7',
  '--accent-ink': '#ffffff',
  '--accent-bg': 'rgba(2,132,199,0.08)',
  '--accent-border': 'rgba(2,132,199,0.18)',
  '--accent-border2': 'rgba(2,132,199,0.12)',
  '--accent-border3': 'rgba(2,132,199,0.2)',
  '--accent-border4': 'rgba(2,132,199,0.22)',
  '--accent-subtle': 'rgba(2,132,199,0.05)',
  '--accent-rgb': '2 132 199',
  '--bg-inset': 'rgba(241,245,249,0.6)',
  '--bg-inset2': 'rgba(226,232,240,0.7)',
  '--danger': '#ef4444',
  '--danger-bg': 'rgba(239,68,68,0.08)',
};

/**
 * Employee portal login — request a magic link by email.
 * Shown when an employee navigates to /portal-login (or clicks "Employee Portal" on LoginScreen).
 */
export default function EmployeeMagicLinkRequest({ requestMagicLink, onBack }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    const result = await requestMagicLink(trimmed);

    setLoading(false);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    setSent(true);
  };

  if (sent) {
    return (
      <div
        style={{
          ...PORTAL_THEME,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} />
            <div className="display" style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg)' }}>
              {getDomainBranding().name}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg3)',
                marginTop: 4,
                fontFamily: 'var(--font-mono)',
              }}
            >
              Employee Portal
            </div>
          </div>

          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(94, 234, 212, 0.1)',
                border: '1px solid rgba(94, 234, 212, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                margin: '0 auto 20px',
              }}
            >
              &#9993;
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg)', marginBottom: 12 }}>
              Check your email
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 20 }}>
              If an employee account exists for{' '}
              <strong style={{ color: 'var(--fg2)' }}>{email}</strong>, we've sent a magic link to
              sign in. The link expires in 15 minutes and can only be used once.
            </div>

            <button
              className="btn"
              style={{ width: '100%', justifyContent: 'center', padding: '10px 0', fontSize: 13 }}
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
            >
              Try a different email
            </button>

            {onBack && (
              <button
                onClick={onBack}
                style={{
                  marginTop: 12,
                  background: 'none',
                  border: 'none',
                  color: 'var(--fg3)',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Back to login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...PORTAL_THEME,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} />
          <div className="display" style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg)' }}>
            {getDomainBranding().name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--fg3)',
              marginTop: 4,
              fontFamily: 'var(--font-mono)',
            }}
          >
            Employee Portal
          </div>
        </div>

        {/* Form */}
        <div className="card" style={{ padding: 32 }}>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
            Employee Portal Sign In
          </div>
          <div style={{ marginBottom: 20, fontSize: 12, color: 'var(--fg3)', lineHeight: 1.5 }}>
            Enter your work email to receive a one-time sign-in link.
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'block',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--fg3)',
                fontFamily: 'var(--font-mono)',
                marginBottom: 6,
              }}
            >
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              className="input"
              placeholder="you@company.com"
              autoFocus
            />
          </div>

          {error && (
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--danger-bg)',
                border: '1px solid rgba(251,113,133,0.25)',
                color: 'var(--danger)',
                fontSize: 12,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Sending link...' : 'Send magic link'}
          </button>

          {onBack && (
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <button
                onClick={onBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--fg3)',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Back to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
